/*
 * Livingston V16 + V17 + V18 + V19: collection filter drawer behaviour.
 *
 * V16 deferred the full collection render until the drawer closes so Horizon
 * could not replace the section and make the drawer disappear after the first
 * selection. V17 added persistent Apply filters / Clear all filters actions.
 * V18 made Clear all close and apply immediately.
 *
 * V19 adds live dependent facets without re-rendering the visible collection.
 * Each local filter change requests the same Shopify section in the background,
 * reads Shopify's recalculated facet availability, and disables impossible
 * unselected choices in the open drawer. Selected values always stay enabled so
 * they can be removed. The product grid still updates once, on Apply/close.
 */

const PATCH_FLAG = '__livingstonDeferredFilterApplyV19';

if (!window[PATCH_FLAG]) {
  window[PATCH_FLAG] = true;

  let dirty = false;
  let applyQueued = false;
  let availabilityController = null;
  let availabilityTimer = 0;
  let availabilityRequestId = 0;

  const getDrawer = () => document.getElementById('filters-drawer');

  function prepareActions(drawer) {
    const actions = drawer.querySelector('.facets__drawer-actions');
    if (!(actions instanceof HTMLElement)) return;

    const clearAll = actions.querySelector('.facets__clear-all');
    if (clearAll instanceof HTMLButtonElement) {
      clearAll.textContent = 'Clear all filters';
      clearAll.setAttribute('aria-label', 'Clear all filters');
    }

    let applyButton = actions.querySelector('.facets__see-results, [data-livingston-apply-filters]');

    // Horizon can omit its native See items button when the current result count
    // is zero. Keep an Apply filters control available anyway so the shopper can
    // revise an old or externally-linked zero-result combination.
    if (!(applyButton instanceof HTMLButtonElement)) {
      applyButton = document.createElement('button');
      applyButton.type = 'button';
      applyButton.className = 'button facets__see-results livingston-filter-apply';
      applyButton.setAttribute('data-livingston-apply-filters', '');
      applyButton.addEventListener('click', () => {
        if ('close' in drawer && typeof drawer.close === 'function') drawer.close();
      });
      actions.appendChild(applyButton);
    }

    applyButton.textContent = 'Apply filters';
    applyButton.classList.add('livingston-filter-apply');
    applyButton.setAttribute('data-livingston-apply-filters', '');
    applyButton.setAttribute('aria-label', 'Apply filters');
  }

  function prepareDrawer() {
    const drawer = getDrawer();
    if (!(drawer instanceof HTMLElement)) return;

    // Keep Horizon from replacing the whole collection section while the drawer
    // is open. V19 refreshes only availability in the background instead.
    for (const element of drawer.querySelectorAll('[on\\:change]')) {
      const handler = element.getAttribute('on:change') ?? '';
      if (
        handler.includes('updateFilters') ||
        handler.includes('updatePriceFilterAndResults') ||
        handler.includes('updateFilterAndSorting')
      ) {
        element.removeAttribute('on:change');
      }
    }

    // Clear all remains local until the drawer closes, just like other changes.
    const clearAll = drawer.querySelector('.facets__clear-all');
    if (clearAll instanceof HTMLElement) {
      clearAll.removeAttribute('on:click');
      clearAll.removeAttribute('on:keydown');
    }

    prepareActions(drawer);
  }

  function countLocalFilters(drawer) {
    const checkedCount = drawer.querySelectorAll('input[type="checkbox"]:checked:not(:disabled)').length;
    const minPrice = drawer.querySelector('input[name="filter.v.price.gte"]');
    const maxPrice = drawer.querySelector('input[name="filter.v.price.lte"]');
    const priceActive =
      (minPrice instanceof HTMLInputElement && minPrice.value.trim() !== '') ||
      (maxPrice instanceof HTMLInputElement && maxPrice.value.trim() !== '');

    return checkedCount + (priceActive ? 1 : 0);
  }

  function syncLocalStatus(drawer) {
    const count = countLocalFilters(drawer);
    const badge = drawer.querySelector('#filters-drawer-bubble');
    if (badge instanceof HTMLElement) {
      badge.textContent = String(count);
      badge.hidden = count === 0;
    }

    const clearAll = drawer.querySelector('.facets__clear-all');
    if (clearAll instanceof HTMLElement) {
      clearAll.classList.toggle('facets__clear-all--active', count > 0);
      if (clearAll instanceof HTMLButtonElement) {
        clearAll.disabled = count === 0;
        clearAll.setAttribute('aria-disabled', String(count === 0));
      }
    }
  }

  function markDirty(drawer) {
    dirty = true;
    syncLocalStatus(drawer);
  }

  function getFacetContext(drawer) {
    const formComponent = drawer.querySelector('facets-form-component');
    if (!(formComponent instanceof HTMLElement)) return null;

    const form = formComponent.querySelector('form') || drawer.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return null;

    const sectionId = formComponent.getAttribute('section-id') || formComponent.sectionId;
    if (!sectionId) return null;

    return { formComponent, form, sectionId };
  }

  function createFallbackParameters(form) {
    const params = new URLSearchParams();
    const data = new FormData(form);

    for (const [key, value] of data.entries()) {
      if (typeof value !== 'string' || key === 'page') continue;
      if (
        (key === 'filter.v.price.gte' || key === 'filter.v.price.lte') &&
        value.trim() === ''
      ) {
        continue;
      }
      params.append(key, value);
    }

    return params;
  }

  function buildAvailabilityUrl(drawer) {
    const context = getFacetContext(drawer);
    if (!context) return null;

    const { formComponent, form, sectionId } = context;
    const formData = new FormData(form);
    const params =
      typeof formComponent.createURLParameters === 'function'
        ? formComponent.createURLParameters(formData)
        : createFallbackParameters(form);

    // Start from the current URL so Shopify preview parameters are preserved on
    // unpublished themes. Replace only parameters managed by the facet form.
    const url = new URL(window.location.href);

    for (const key of [...url.searchParams.keys()]) {
      if (
        key.startsWith('filter.') ||
        key === 'sort_by' ||
        key === 'page' ||
        key === 'section_id'
      ) {
        url.searchParams.delete(key);
      }
    }

    for (const key of new Set(params.keys())) {
      url.searchParams.delete(key);
    }

    for (const [key, value] of params.entries()) {
      if (key !== 'page' && key !== 'section_id') url.searchParams.append(key, value);
    }

    url.searchParams.set('section_id', String(sectionId));
    return url;
  }

  function inputKey(input) {
    return `${input.name}\u0000${input.value}`;
  }

  function setOptionUnavailable(input, unavailable) {
    if (!(input instanceof HTMLInputElement)) return;

    input.disabled = unavailable;
    if (unavailable) input.setAttribute('aria-disabled', 'true');
    else input.removeAttribute('aria-disabled');

    const listItem = input.closest('.facets__inputs-list-item');
    if (listItem instanceof HTMLElement) {
      listItem.classList.toggle('facets__inputs-list-item--disabled', unavailable);
      if (unavailable) listItem.setAttribute('aria-disabled', 'true');
      else listItem.removeAttribute('aria-disabled');
    }

    const checkbox = input.closest('.checkbox');
    if (checkbox instanceof HTMLElement) {
      checkbox.classList.toggle('checkbox--disabled', unavailable);
    }

    const swatch = input.closest('.variant-option--swatches');
    if (swatch instanceof HTMLElement) {
      swatch.classList.toggle('variant-option--swatches-disabled', unavailable);
    }
  }

  function syncFacetAvailability(drawer, freshDrawer) {
    const freshInputs = new Map();

    for (const input of freshDrawer.querySelectorAll('input[type="checkbox"][name]')) {
      freshInputs.set(inputKey(input), input);
    }

    for (const input of drawer.querySelectorAll('input[type="checkbox"][name]')) {
      if (!(input instanceof HTMLInputElement)) continue;

      const freshInput = freshInputs.get(inputKey(input));
      const serverUnavailable =
        !freshInput ||
        freshInput.hasAttribute('disabled') ||
        freshInput.getAttribute('aria-disabled') === 'true';

      // A selected value must always remain clickable so it can be removed,
      // even if the fresh response reports zero matching items for that value.
      setOptionUnavailable(input, !input.checked && serverUnavailable);
    }

    syncLocalStatus(drawer);
  }

  function cancelAvailabilityRefresh(drawer = getDrawer()) {
    if (availabilityTimer) {
      window.clearTimeout(availabilityTimer);
      availabilityTimer = 0;
    }

    if (availabilityController) {
      availabilityController.abort();
      availabilityController = null;
    }

    availabilityRequestId += 1;
    if (drawer instanceof HTMLElement) {
      drawer.removeAttribute('data-livingston-facet-refreshing');
      drawer.removeAttribute('aria-busy');
    }
  }

  async function refreshFacetAvailability(drawer) {
    if (!(drawer instanceof HTMLElement) || !drawer.isConnected) return;

    const url = buildAvailabilityUrl(drawer);
    if (!(url instanceof URL)) return;

    if (availabilityController) availabilityController.abort();

    const controller = new AbortController();
    availabilityController = controller;
    const requestId = ++availabilityRequestId;

    drawer.setAttribute('data-livingston-facet-refreshing', 'true');
    drawer.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(url.toString(), {
        signal: controller.signal,
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) throw new Error(`Facet availability request failed: ${response.status}`);

      const html = await response.text();
      if (controller.signal.aborted || requestId !== availabilityRequestId || !drawer.isConnected) return;

      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const freshDrawer = documentFragment.getElementById('filters-drawer');
      if (!(freshDrawer instanceof Element)) return;

      syncFacetAvailability(drawer, freshDrawer);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.warn('[Livingston filters] Live facet availability refresh failed.', error);
    } finally {
      if (requestId === availabilityRequestId && drawer.isConnected) {
        drawer.removeAttribute('data-livingston-facet-refreshing');
        drawer.removeAttribute('aria-busy');
      }
      if (availabilityController === controller) availabilityController = null;
    }
  }

  function scheduleAvailabilityRefresh(drawer, delay = 0) {
    if (availabilityTimer) window.clearTimeout(availabilityTimer);

    availabilityTimer = window.setTimeout(() => {
      availabilityTimer = 0;
      refreshFacetAvailability(drawer);
    }, delay);
  }

  function applyWhenFullyClosed(drawer) {
    if (applyQueued) return;
    applyQueued = true;

    const waitForClose = () => {
      const dialog = drawer.querySelector('dialog');
      const stillOpen = drawer.hasAttribute('open') || (dialog instanceof HTMLDialogElement && dialog.open);

      if (stillOpen) {
        requestAnimationFrame(waitForClose);
        return;
      }

      applyQueued = false;
      if (!dirty) return;

      const formComponent = drawer.querySelector('facets-form-component');
      if (!(formComponent instanceof HTMLElement) || typeof formComponent.updateFilters !== 'function') return;

      dirty = false;
      formComponent.updateFilters();
    };

    requestAnimationFrame(waitForClose);
  }

  // Prepare immediately for the initial render.
  prepareDrawer();

  // Section renders can restore Horizon's native handlers. Prepare the drawer
  // every time it opens so the local/deferred behaviour is always reinstated.
  document.addEventListener('theme-drawer:open', (event) => {
    if (!(event.target instanceof HTMLElement) || event.target.id !== 'filters-drawer') return;
    prepareDrawer();
    syncLocalStatus(event.target);
  });

  // Local checkbox/price/sort changes mark the drawer dirty. Facet and price
  // changes also refresh Shopify's server-calculated availability in place.
  document.addEventListener(
    'change',
    (event) => {
      const drawer = getDrawer();
      if (!(drawer instanceof HTMLElement) || !(event.target instanceof Node) || !drawer.contains(event.target)) return;

      markDirty(drawer);

      if (!(event.target instanceof HTMLInputElement)) return;

      const isFacetCheckbox = event.target.type === 'checkbox' && event.target.name.startsWith('filter.');
      const isPriceInput =
        event.target.name === 'filter.v.price.gte' ||
        event.target.name === 'filter.v.price.lte';

      if (isFacetCheckbox) scheduleAvailabilityRefresh(drawer, 0);
      else if (isPriceInput) scheduleAvailabilityRefresh(drawer, 160);
    },
    true
  );

  // Clear all locally, cancel any pending availability request, close the drawer,
  // then let the normal close handler apply the cleared form once.
  document.addEventListener('click', (event) => {
    const drawer = getDrawer();
    if (!(drawer instanceof HTMLElement) || !(event.target instanceof Element)) return;

    const clearAll = event.target.closest('.facets__clear-all');
    if (!(clearAll instanceof HTMLElement) || !drawer.contains(clearAll)) return;

    event.preventDefault();

    for (const input of drawer.querySelectorAll('input[type="checkbox"]')) {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        setOptionUnavailable(input, false);
      }
    }

    for (const input of drawer.querySelectorAll('input[name="filter.v.price.gte"], input[name="filter.v.price.lte"]')) {
      if (input instanceof HTMLInputElement) input.value = '';
    }

    cancelAvailabilityRefresh(drawer);
    markDirty(drawer);

    if ('close' in drawer && typeof drawer.close === 'function') drawer.close();
  });

  // Apply filters, Clear all filters, X, Escape and backdrop close all converge
  // here. Apply once the drawer animation has finished and it is genuinely closed.
  document.addEventListener('theme-drawer:close', (event) => {
    if (!(event.target instanceof HTMLElement) || event.target.id !== 'filters-drawer') return;
    cancelAvailabilityRefresh(event.target);
    applyWhenFullyClosed(event.target);
  });
}
